import React from 'react';
import { Box } from '@mui/material';
import {
 School,
 MenuBook,
 Psychology,
 EmojiEvents,
 Groups,
 Lightbulb,
 Quiz,
 Assignment,
 Timeline,
 Star,
 AutoStories,
 Science,
 Calculate,
 Language,
 Brush,
 Computer,
 SportsSoccer,
 MusicNote
} from '@mui/icons-material';

interface LearningVisualElementsProps {
 primaryColor: string;
 isDark: boolean;
}

const LearningVisualElements: React.FC<LearningVisualElementsProps> = ({ primaryColor, isDark }) => {
const iconColor = isDark ? 'rgba(255, 255, 255, 0.3)' : primaryColor; 
 return (
   <>
     
     <Box
       sx={{
         position: 'absolute',
         top: '8%',
         left: '15%',
         color: iconColor,
         opacity: isDark ? 0.08 : 0.06,
         zIndex: 0,
         animation: 'floatLearn1 8s ease-in-out infinite',
         '@keyframes floatLearn1': {
           '0%, 100%': { transform: 'translate3d(0, 0, 0) rotate(0deg) scale(1)' },
           '33%': { transform: 'translate3d(15px, -10px, 0) rotate(3deg) scale(1.05)' },
           '66%': { transform: 'translate3d(-8px, 15px, 0) rotate(-2deg) scale(0.95)' }
         }
       }}
     >
       <School sx={{ fontSize: { xs: 30, sm: 38, md: 46 } }} />
     </Box>

     <Box
       sx={{
         position: 'absolute',
         top: '15%',
         right: '20%',
         color: iconColor,
         opacity: isDark ? 0.07 : 0.05,
         zIndex: 0,
         animation: 'floatLearn2 10s ease-in-out infinite 1s',
         '@keyframes floatLearn2': {
           '0%, 100%': { transform: 'translate3d(0, 0, 0) rotate(0deg)' },
           '50%': { transform: 'translate3d(-18px, 12px, 0) rotate(6deg)' }
         }
       }}
     >
       <MenuBook sx={{ fontSize: { xs: 26, sm: 34, md: 42 } }} />
     </Box>

     <Box
       sx={{
         position: 'absolute',
         top: '25%',
         left: '8%',
         color: iconColor,
         opacity: isDark ? 0.06 : 0.04,
         zIndex: 0,
         animation: 'floatLearn3 12s ease-in-out infinite 2s',
         '@keyframes floatLearn3': {
           '0%, 100%': { transform: 'translate3d(0, 0, 0) scale(1)' },
           '25%': { transform: 'translate3d(12px, -15px, 0) scale(1.08)' },
           '75%': { transform: 'translate3d(-15px, 8px, 0) scale(0.92)' }
         }
       }}
     >
       <Science sx={{ fontSize: { xs: 28, sm: 36, md: 44 } }} />
     </Box>

     <Box
       sx={{
         position: 'absolute',
         top: '40%',
         right: '12%',
         color: iconColor,
         opacity: isDark ? 0.09 : 0.07,
         zIndex: 0,
         animation: 'floatLearn4 9s ease-in-out infinite 1.5s',
       }}
     >
       <Calculate sx={{ fontSize: { xs: 24, sm: 32, md: 40 } }} />
     </Box>

     <Box
       sx={{
         position: 'absolute',
         bottom: '35%',
         left: '18%',
         color: iconColor,
         opacity: isDark ? 0.07 : 0.05,
         zIndex: 0,
         animation: 'floatLearn5 11s ease-in-out infinite 3s',
         '@keyframes floatLearn5': {
           '0%, 100%': { transform: 'translate3d(0, 0, 0) rotate(0deg)' },
           '33%': { transform: 'translate3d(20px, -8px, 0) rotate(-5deg)' },
           '66%': { transform: 'translate3d(-12px, 20px, 0) rotate(3deg)' }
         }
       }}
     >
       <Computer sx={{ fontSize: { xs: 29, sm: 37, md: 45 } }} />
     </Box>

     <Box
       sx={{
         position: 'absolute',
         top: '55%',
         right: '25%',
         color: iconColor,
         opacity: isDark ? 0.06 : 0.04,
         zIndex: 0,
         animation: 'floatLearn6 13s ease-in-out infinite 2.5s',
       }}
     >
       <Language sx={{ fontSize: { xs: 25, sm: 33, md: 41 } }} />
     </Box>

     <Box
       sx={{
         position: 'absolute',
         bottom: '20%',
         right: '30%',
         color: iconColor,
         opacity: isDark ? 0.08 : 0.06,
         zIndex: 0,
         animation: 'floatLearn7 7s ease-in-out infinite 4s',
       }}
     >
       <Brush sx={{ fontSize: { xs: 22, sm: 30, md: 38 } }} />
     </Box>

     <Box
       sx={{
         position: 'absolute',
         top: '20%',
         left: '40%',
         color: iconColor,
         opacity: isDark ? 0.1 : 0.08,
         zIndex: 0,
         animation: 'achievementFloat 15s ease-in-out infinite',
         '@keyframes achievementFloat': {
           '0%, 100%': { transform: 'translate3d(0, 0, 0) rotate(0deg) scale(1)' },
           '20%': { transform: 'translate3d(8px, -12px, 0) rotate(10deg) scale(1.06)' },
           '40%': { transform: 'translate3d(-6px, 10px, 0) rotate(-8deg) scale(0.94)' },
           '60%': { transform: 'translate3d(12px, 6px, 0) rotate(12deg) scale(1.04)' },
           '80%': { transform: 'translate3d(-10px, -8px, 0) rotate(-4deg) scale(0.96)' }
         }
       }}
     >
       <EmojiEvents sx={{ fontSize: { xs: 32, sm: 40, md: 48 } }} />
     </Box>

     <Box
       sx={{
         position: 'absolute',
         bottom: '45%',
         left: '25%',
         color: iconColor,
         opacity: isDark ? 0.08 : 0.06,
         zIndex: 0,
         animation: 'lightbulbGlow 6s ease-in-out infinite',
         '@keyframes lightbulbGlow': {
           '0%, 100%': { 
             transform: 'scale(1)',
             filter: `drop-shadow(0 0 3px ${iconColor}20)`
           },
           '50%': { 
             transform: 'scale(1.1)',
             filter: `drop-shadow(0 0 8px ${iconColor}30)`
           }
         }
       }}
     >
       <Lightbulb sx={{ fontSize: { xs: 30, sm: 38, md: 46 } }} />
     </Box>

     <Box
       sx={{
         position: 'absolute',
         top: '65%',
         left: '6%',
         color: iconColor,
         opacity: isDark ? 0.07 : 0.05,
         zIndex: 0,
         animation: 'quizPulse 8s ease-in-out infinite 1s',
         '@keyframes quizPulse': {
           '0%, 100%': { transform: 'scale(1) rotate(0deg)' },
           '25%': { transform: 'scale(1.15) rotate(4deg)' },
           '50%': { transform: 'scale(0.95) rotate(-2deg)' },
           '75%': { transform: 'scale(1.08) rotate(2deg)' }
         }
       }}
     >
       <Quiz sx={{ fontSize: { xs: 27, sm: 35, md: 43 } }} />
     </Box>

     <Box
       sx={{
         position: 'absolute',
         bottom: '28%',
         right: '8%',
         color: iconColor,
         opacity: isDark ? 0.09 : 0.07,
         zIndex: 0,
         animation: 'assignmentFloat 10s ease-in-out infinite 3s',
       }}
     >
       <Assignment sx={{ fontSize: { xs: 23, sm: 31, md: 39 } }} />
     </Box>

     <Box
       sx={{
         position: 'absolute',
         bottom: '50%',
         right: '18%',
         color: iconColor,
         opacity: isDark ? 0.08 : 0.06,
         zIndex: 0,
         animation: 'timelineProgress 14s ease-in-out infinite 2s',
         '@keyframes timelineProgress': {
           '0%, 100%': { transform: 'translateX(0) scaleY(1)' },
           '33%': { transform: 'translateX(15px) scaleY(1.05)' },
           '66%': { transform: 'translateX(-12px) scaleY(0.95)' }
         }
       }}
     >
       <Timeline sx={{ fontSize: { xs: 31, sm: 39, md: 47 } }} />
     </Box>

     <Box
       sx={{
         position: 'absolute',
         top: '45%',
         left: '35%',
         color: iconColor,
         opacity: isDark ? 0.09 : 0.07,
         zIndex: 0,
         animation: 'groupCollaboration 12s ease-in-out infinite 4s',
         '@keyframes groupCollaboration': {
           '0%, 100%': { transform: 'translate3d(0, 0, 0) scale(1)' },
           '50%': { transform: 'translate3d(25px, -15px, 0) scale(1.08)' }
         }
       }}
     >
       <Groups sx={{ fontSize: { xs: 34, sm: 42, md: 50 } }} />
     </Box>

     <Box
       sx={{
         position: 'absolute',
         top: '5%',
         left: '60%',
         color: iconColor,
         opacity: isDark ? 0.05 : 0.03,
         zIndex: 0,
         animation: 'starTwinkle 5s ease-in-out infinite',
         '@keyframes starTwinkle': {
           '0%, 100%': { opacity: isDark ? 0.05 : 0.03, transform: 'scale(1) rotate(0deg)' },
           '50%': { opacity: isDark ? 0.12 : 0.08, transform: 'scale(1.2) rotate(180deg)' }
         }
       }}
     >
       <Star sx={{ fontSize: { xs: 16, sm: 21, md: 26 } }} />
     </Box>

     <Box
       sx={{
         position: 'absolute',
         bottom: '12%',
         left: '50%',
         color: iconColor,
         opacity: isDark ? 0.06 : 0.04,
         zIndex: 0,
         animation: 'starTwinkle 7s ease-in-out infinite 2s',
       }}
     >
       <Star sx={{ fontSize: { xs: 12, sm: 16, md: 20 } }} />
     </Box>

     <Box
       sx={{
         position: 'absolute',
         top: '35%',
         right: '35%',
         color: iconColor,
         opacity: isDark ? 0.04 : 0.03,
         zIndex: 0,
         animation: 'starTwinkle 6s ease-in-out infinite 4s',
       }}
     >
       <Star sx={{ fontSize: { xs: 14, sm: 18, md: 22 } }} />
     </Box>

     <Box
       sx={{
         position: 'absolute',
         top: '75%',
         right: '40%',
         color: iconColor,
         opacity: isDark ? 0.06 : 0.04,
         zIndex: 0,
         animation: 'sportsActivity 9s ease-in-out infinite 1s',
       }}
     >
       <SportsSoccer sx={{ fontSize: { xs: 21, sm: 29, md: 37 } }} />
     </Box>

     <Box
       sx={{
         position: 'absolute',
         bottom: '55%',
         left: '65%',
         color: iconColor,
         opacity: isDark ? 0.08 : 0.06,
         zIndex: 0,
         animation: 'musicNote 11s ease-in-out infinite 3.5s',
         '@keyframes musicNote': {
           '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
           '25%': { transform: 'translateY(-12px) rotate(8deg)' },
           '50%': { transform: 'translateY(8px) rotate(-4deg)' },
           '75%': { transform: 'translateY(-6px) rotate(6deg)' }
         }
       }}
     >
       <MusicNote sx={{ fontSize: { xs: 20, sm: 28, md: 36 } }} />
     </Box>

     <Box
       sx={{
         position: 'absolute',
         top: '30%',
         right: '45%',
         color: iconColor,
         opacity: isDark ? 0.07 : 0.05,
         zIndex: 0,
         animation: 'bookPages 13s ease-in-out infinite 2.5s',
         '@keyframes bookPages': {
           '0%, 100%': { transform: 'rotateY(0deg) scale(1)' },
           '33%': { transform: 'rotateY(15deg) scale(1.04)' },
           '66%': { transform: 'rotateY(-12deg) scale(0.96)' }
         }
       }}
     >
       <AutoStories sx={{ fontSize: { xs: 26, sm: 34, md: 42 } }} />
     </Box>

     <Box
       sx={{
         position: 'absolute',
         bottom: '65%',
         right: '5%',
         color: iconColor,
         opacity: isDark ? 0.06 : 0.04,
         zIndex: 0,
         animation: 'psychologyFloat 16s ease-in-out infinite 5s',
         '@keyframes psychologyFloat': {
           '0%, 100%': { transform: 'translate3d(0, 0, 0) rotate(0deg)' },
           '33%': { transform: 'translate3d(-18px, 10px, 0) rotate(-6deg)' },
           '66%': { transform: 'translate3d(15px, -12px, 0) rotate(4deg)' }
         }
       }}
     >
       <Psychology sx={{ fontSize: { xs: 24, sm: 32, md: 40 } }} />
     </Box>

     <Box
       sx={{
         position: 'absolute',
         top: '25%',
         left: '25%',
         width: '50%',
         height: '50%',
         background: `radial-gradient(ellipse, ${iconColor}02, transparent 60%)`,
         borderRadius: '50%',
         zIndex: 0,
         animation: 'learningNetwork 20s ease-in-out infinite',
         '@keyframes learningNetwork': {
           '0%, 100%': { 
             transform: 'scale(1) rotate(0deg)',
             opacity: isDark ? 0.15 : 0.08
           },
           '50%': { 
             transform: 'scale(1.05) rotate(180deg)',
             opacity: isDark ? 0.08 : 0.04
           }
         }
       }}
     />
   </>
 );
};

export default LearningVisualElements;